import React from "react";
import { useHistory } from "react-router-dom";
import { Col, Row, Form, Button, Skeleton, Modal, message } from "antd";
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";

import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";
import FormInput from "components/FormInput/FormInput";
import FormSelection from "components/FormSelection/FormSelection";
import FormGroupSelectionInput from "components/FormGroupSelectionInput/FormGroupSelectionInput";
import ScrollableContainer from "components/ScrollableContainer/ScrollableContainer";
import FormUpload from "components/Upload/FormUpload/FormUpload";
import SectionCard from "components/SectionCard/SectionCard";
import Title from "components/Title/Title";

import { useFetchConstant } from "apis/constant";
import { patchUpdateUser } from "apis/user";
import { withAppContext } from "contexts/AppContext/AppContext";

import { SectionContainer } from "./Profile.styles";
import { env } from "utils/constants";

import Locale from "react-intl-universal";

const { useForm } = Form;
const { error, confirm } = Modal;

const Profile = ({
  isUserLoading,
  getLoggedInUser,
  refetchLoggedInUserProfile,
}) => {
  const history = useHistory();
  const [form] = useForm();
  const loggedInUser = getLoggedInUser();
  const {
    isLoading: isCountryCodesLoading,
    selection: countryCodes,
  } = useFetchConstant("countries", "phoneCode", "phoneCode");
  const { isLoading: isRolesLoading, selection: roles } = useFetchConstant(
    "roles"
  );

  const isLoading = isUserLoading || isCountryCodesLoading || isRolesLoading;

  const onBackButtonClick = () => history.goBack();

  const handleOnSaveButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
      };

      confirm({
        title: Locale.get("Profile.confirm_title.headerTitle"),
        content: Locale.get("Profile.confirm_title.confirmContent"),
        icon: <ExclamationCircleOutlined />,
        onOk() {
          patchUpdateUser(loggedInUser._id, payload)
            .then(() => {
              message.success(
                Locale.get("Profile.confirm_title.successContent")
              );
              refetchLoggedInUserProfile();
            })
            .catch((ex) => {
              error({
                title: ex.message,
              });
            });
        },
        onCancel() {},
      });
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const handleGenerateReferralLink = () => {
    const email = form.getFieldValue("email");

    let link = window.location.origin;

    let copyableText;

    copyableText = `${link}/sign-up?referral=${email}`;

    navigator.clipboard
      .writeText(copyableText)
      .then(() => {
        console.log("Link copied to clipboard");
        message.success(Locale.get("Profile.confirm_title.grlMsgSuccess"));
      })
      .catch((error) => {
        console.error("Error copying text: ", error);
      });
  };

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} initialValues={loggedInUser}>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: "32px" }}
          >
            <ClickableTextButton
              text="Back"
              onClick={onBackButtonClick}
              showIcon={true}
              type="back"
            />
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleOnSaveButtonClick}
            >
              {Locale.get("Profile.saveButton")}
            </Button>
          </Row>
          <ScrollableContainer>
            <Title marginBottom="8px">{Locale.get("Profile.pageTitle")}</Title>
            <Row justify="end" align="middle" style={{ marginBottom: "16px" }}>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleGenerateReferralLink}
              >
                {Locale.get("Profile.grl")}
              </Button>
            </Row>
            <SectionContainer>
              <SectionCard title={Locale.get("Profile.cardTitle_1")}>
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.labelFName")}
                      name={["name", "firstName"]}
                      placeholder="John"
                      requiredErrorMessage="Please enter your first name."
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.labelLName")}
                      name={["name", "lastName"]}
                      placeholder="Doe"
                      requiredErrorMessage="Please enter your last name."
                    />
                  </Col>
                  <Col span={12}>
                    <FormGroupSelectionInput
                      label={Locale.get("Profile.contactNo")}
                      inputName={["contact", "contactNumber"]}
                      selectionName={["contact", "countryCode"]}
                      inputPlaceholder={Locale.get(
                        "Profile.placeHolderOptional"
                      )}
                      inputType="number"
                      selections={countryCodes}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.email")}
                      name="email"
                      placeholder="your_email@gmail.com"
                      type="email"
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
            <SectionContainer>
              <SectionCard title={Locale.get("Profile.cardTitle_2")}>
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.labelCompanyName")}
                      name={["company", "name"]}
                      requiredErrorMessage="Please enter your company name."
                    />
                  </Col>
                  <Col span={12}>
                    <FormUpload
                      label={Locale.get("Profile.logo")}
                      name={["company", "logo"]}
                      acceptTypes={["image/*"]}
                      isDisplayCard
                      labelButtonUpload={Locale.get("Profile.upload")}
                      fileSizeLimitInMb={1}
                      tooltipMessage="Use square size logo. Best size: 128px * 128px"
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
            <SectionContainer>
              <SectionCard title={Locale.get("Profile.cardTitle_3")}>
                <Row gutter={8}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.labelUsername")}
                      name="username"
                      disabled={true}
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelection
                      label={Locale.get("Profile.labelRole")}
                      name="role"
                      selections={roles}
                      disabled={true}
                    />
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Profile.labelReferral")}
                      name="referral"
                      disabled={true}
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
          </ScrollableContainer>
        </Form>
      )}
    </>
  );
};

export default withAppContext(Profile);
